import { Button, Callout, Icon, Skeleton } from '@wtfalch/design';
import { useEffect, useState } from 'react';

import { dueLabel, longDay } from './dates.ts';
import { Quick } from './Quick.tsx';
import { Tick, Who } from './Rows.tsx';
import {
  type FromKind,
  MINE,
  type Task,
  type TaskDetail,
  type TaskList,
  type TasksSource,
} from './source.ts';

const FROM_WORDS: Record<FromKind, string> = {
  'chat.message': 'Chat message',
  'chat.call': 'Call',
  'mail.thread': 'Mail thread',
  'calendar.event': 'Event',
};

/** One task, opened over its list: who, when, where it came from, its steps and notes. */
export function TaskPage({
  source,
  taskId,
  lists,
  today,
  version,
  onClose,
  onChanged,
}: {
  source: TasksSource;
  taskId: string;
  lists: readonly TaskList[];
  today: string;
  version: number;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let live = true;
    // Both are read to re-run this when the list changes under the page.
    void version;
    void reload;
    source
      .task(taskId)
      .then((t) => {
        if (!live) return;
        setTask(t);
        setError(null);
      })
      .catch((err: unknown) => {
        // Not the SDK's own message: a wire error's text is the service's,
        // not written for a person to read (agora#486). Logged (agora#498)
        // so the failure leaves a trace even though the text on screen stays
        // generic.
        console.error(err);
        if (live) setError('This task could not be opened.');
      });
    return () => {
      live = false;
    };
  }, [source, taskId, version, reload]);

  const change = async (run: () => Promise<unknown>) => {
    try {
      await run();
      setReload((n) => n + 1);
      onChanged();
    } catch {
      setError('The change was not saved.');
    }
  };

  const listName = lists.find((l) => l.id === task?.listId)?.name ?? '';

  return (
    <aside className="tasks-page" aria-label="Task">
      <div className="tasks-page-bar">
        <Button kind="ghost" size="sm" iconOnly aria-label="Close task" onPress={onClose}>
          <Icon name="close" size={16} />
        </Button>
      </div>
      {error && <Callout tone="bad">{error}</Callout>}
      {!task ? (
        error ? null : (
          <Skeleton lines={4} />
        )
      ) : (
        <>
          <div className="tasks-page-title">
            <Tick task={task} onToggle={(done) => void change(() => source.setDone(task, done))} />
            <h2>{task.title}</h2>
          </div>

          <dl className="tasks-facts">
            <dt>Who</dt>
            <dd className="tasks-fact-who">
              <Who person={task.assignee} size="sm" />
              {task.assignee ? task.assignee.name : 'Nobody yet'}
              {task.assignee?.me && <span className="tasks-you">you</span>}
            </dd>
            <dt>When</dt>
            <dd>
              {task.dueOn ? (
                <>
                  {dueLabel(task.dueOn, today)}
                  <span className="tasks-you">{longDay(task.dueOn)}</span>
                </>
              ) : (
                'No date'
              )}
            </dd>
            {task.from && (
              <>
                <dt>Came from</dt>
                <dd className="tasks-fact-from">
                  {task.from.label}
                  <span className="tasks-you">{FROM_WORDS[task.from.kind]}</span>
                </dd>
              </>
            )}
          </dl>

          <section aria-label="Steps" className="tasks-page-section">
            <h3 className="tasks-group-head">
              Steps
              {task.stepList.length > 0 && (
                <span className="tasks-count">
                  {task.stepList.filter((s) => s.done).length} of {task.stepList.length}
                </span>
              )}
            </h3>
            <ul className="tasks-rows">
              {task.stepList.map((step: Task) => (
                <li key={step.id} className="tasks-row" data-done={step.done || undefined}>
                  <Tick
                    task={step}
                    onToggle={(done) => void change(() => source.setDone(step, done))}
                  />
                  <span className="tasks-row-title">{step.title}</span>
                </li>
              ))}
            </ul>
            <Quick
              label="Add a step"
              placeholder="Add a step"
              today={today}
              onAdd={async (step) => {
                await source.addStep(task.id, step.title);
                setReload((n) => n + 1);
                onChanged();
              }}
            />
          </section>

          <section aria-label="Notes" className="tasks-page-section">
            <h3 className="tasks-group-head">Notes</h3>
            {task.notes ? (
              <p className="tasks-notes">{task.notes}</p>
            ) : (
              <p className="tasks-empty-line">No notes.</p>
            )}
          </section>

          {task.listId !== MINE && (
            <section aria-label="More" className="tasks-page-section">
              <h3 className="tasks-group-head">More</h3>
              <dl className="tasks-facts">
                <dt>List</dt>
                <dd>{listName}</dd>
                <dt>Status</dt>
                <dd>{task.statusName}</dd>
                <dt>Key</dt>
                <dd className="tasks-key">{task.key}</dd>
              </dl>
              <p className="tasks-empty-line">
                Shown because this task belongs to {listName}. A personal task has no list, status
                or key to show.
              </p>
            </section>
          )}
        </>
      )}
    </aside>
  );
}
